import { useEffect, useRef, useCallback } from 'react';

/**
 * ApiPollingService
 * 
 * Handles:
 * - Periodic API polling (onPoll callback)
 * - Inactivity-based auto-logout
 * - Proper cleanup of event listeners and timers
 * 
 * Token refresh is handled by AuthContext (proactive) and apiClient (reactive).
 * This service no longer duplicates that logic.
 */
const ApiPollingService = ({ isLoggedIn, onPoll, onLogout }) => {
  const intervalId = useRef(null);
  const initialTimeoutId = useRef(null);
  const lastActivityTime = useRef(Date.now());
  const autoLogoutId = useRef(null);
  const activityListenersAttached = useRef(false);
  
  const INACTIVITY_TIMEOUT = 55 * 60 * 1000; // 55 minutes
  const POLLING_INTERVAL = 55 * 60 * 1000; // 55 minutes
  const INACTIVITY_CHECK_INTERVAL = 60 * 1000; // 1 minute

  /**
   * Update last activity time
   */
  const updateActivity = useCallback(() => {
    lastActivityTime.current = Date.now();
  }, []);

  /**
   * Check for user inactivity and logout if needed
   */
  const checkInactivity = useCallback(() => {
    const currentTime = Date.now();
    const inactiveTime = currentTime - lastActivityTime.current;

    if (inactiveTime >= INACTIVITY_TIMEOUT) {
      onLogout();
    }
  }, [onLogout, INACTIVITY_TIMEOUT]);

  /**
   * Setup activity tracking listeners
   */
  useEffect(() => {
    if (!isLoggedIn || activityListenersAttached.current) {
      return;
    }

    const events = [
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'mousemove',
      'click'
    ];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });
    
    activityListenersAttached.current = true;

    // Cleanup: Remove event listeners
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
      activityListenersAttached.current = false;
    };
  }, [isLoggedIn, updateActivity]);

  /**
   * Setup polling and inactivity checking
   */
  useEffect(() => {
    if (!isLoggedIn) {
      // Clean up all intervals and timeouts when logged out
      if (intervalId.current) {
        clearInterval(intervalId.current);
        intervalId.current = null;
      }
      if (initialTimeoutId.current) {
        clearTimeout(initialTimeoutId.current);
        initialTimeoutId.current = null;
      }
      if (autoLogoutId.current) {
        clearInterval(autoLogoutId.current);
        autoLogoutId.current = null;
      }
      return;
    }

    // AuthContext owns the initial protected-data request. Poll only after the interval.
    lastActivityTime.current = Date.now();

    intervalId.current = setInterval(() => {
      onPoll();
    }, POLLING_INTERVAL);

    // Start inactivity checker
    autoLogoutId.current = setInterval(() => {
      checkInactivity();
    }, INACTIVITY_CHECK_INTERVAL);

    // Cleanup function
    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
        intervalId.current = null;
      }
      if (initialTimeoutId.current) {
        clearTimeout(initialTimeoutId.current);
        initialTimeoutId.current = null;
      }
      if (autoLogoutId.current) {
        clearInterval(autoLogoutId.current);
        autoLogoutId.current = null;
      }
    };
  }, [
    isLoggedIn, 
    onPoll, 
    onLogout, 
    checkInactivity,
    POLLING_INTERVAL,
    INACTIVITY_CHECK_INTERVAL,
  ]);

  return null;
};

export default ApiPollingService;